import React, { createContext, useContext, useId } from "react";
import { Link } from "react-router-dom";
import { CheckIcon, ChevronRightIcon } from "lucide-react";
import styled, { css } from "styled-components/macro";

import { Badge } from "./ui/badge";

// The legacy twin of src/redesign/components/tray.jsx (redesign-tailwind-theme). Keep the
// exports, props and Mark's values the same, so moving the probate page to the redesign is an
// import change. Each --tray-* property falls back to Mark's value; src/probate/theme.css sets
// the ones the probate design canvas changes, and says why.
// Departure: headingId, which names the tray's region by its heading.
// Departure: TrayHeader's headingAs, so a tray that heads its page can be its h1.
// Departure: TrayHeader's count is shadcn's Badge as it ships, a pill, where Mark draws a circle.
// Departure: TrayCell's href is a router path, rendered as a react-router Link.
// Departure: trayVariants isn't exported; nothing here styles another element as a tray.
// Departure: a disabled TrayCell, which Mark doesn't draw, has muted text and no chevron.
// Departure: a TrayCell with no href, onClick or disabled is plain text, where Mark's is a button.
// Departure: TrayCell's icon fills the step's circle, such as with a tick, and draws no line.
// Departure: an outlined variant, the client app redesign's task on a request page.
// Departure: a card tray's title can be sized apart from its header label (--tray-card-title-*).
// Departure: a TrayCell that opens underlines its title on hover, where Mark's has no hover state, and its chevron can change colour.

const TrayContext = createContext({});

// Mark's tray inherits Tailwind's 16px body text; legacy's body text is larger
const StyledTray = styled.section`
  display: flex;
  flex-direction: column;
  gap: var(--tray-gap, var(--juno-space-4));
  padding: var(--tray-padding, var(--juno-space-6));
  border-radius: var(--tray-radius, var(--radius-xl));
  font-size: var(--text-base);
  line-height: var(--text-base--line-height);

  /* Not padding-inline, which needs Safari 14.1 */
  > :not([data-slot="tray"]) {
    padding-right: var(--tray-inset, 0);
    padding-left: var(--tray-inset, 0);
  }

  /* A tray inside a tray is one of its sections, so it takes the section's inset and radius */
  & & {
    padding: 0;
    border-radius: calc(var(--tray-radius, var(--radius-xl)) - var(--tray-padding, var(--juno-space-6)));
  }

  &[data-variant="card"] {
    background-color: var(--card);
    color: var(--card-foreground);
  }

  &[data-variant="card"] > [data-slot="tray-header"] h2 {
    font-size: var(--tray-card-title-font-size, inherit);
    line-height: var(--tray-card-title-line-height, inherit);
    font-weight: var(--tray-card-title-font-weight, inherit);
    letter-spacing: var(--tray-card-title-letter-spacing, normal);
  }

  &[data-variant="secondary"] {
    background-color: var(--secondary);
    color: var(--secondary-foreground);
  }

  &[data-variant="muted"] {
    background-color: var(--muted);
    color: var(--foreground);
  }

  &[data-variant="outlined"] {
    border: 2px solid var(--border);
  }
`;

export function Tray({ variant = "default", headingId: givenHeadingId, children, ...props }) {
  const generatedHeadingId = useId();
  const headingId = givenHeadingId ?? generatedHeadingId;
  return (
    <TrayContext value={{ headingId }}>
      <StyledTray data-slot="tray" data-variant={variant} aria-labelledby={headingId} {...props}>
        {children}
      </StyledTray>
    </TrayContext>
  );
}

const StyledHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--juno-space-4);
  padding-top: var(--tray-header-padding-top, 0);
  padding-bottom: var(--tray-header-padding-bottom, 0);
  font-size: var(--text-sm);
  line-height: var(--text-sm--line-height);

  > div {
    display: flex;
    align-items: baseline;
    gap: var(--juno-space-2);
  }

  h1,
  h2 {
    margin: 0;
    font-size: inherit;
    line-height: inherit;
    font-weight: inherit;
  }
`;

const HeaderStatus = styled.span`
  color: var(--muted-foreground);
`;

export function TrayHeader({ title, status, count, headingAs: Heading = "h2", ...props }) {
  const { headingId } = useContext(TrayContext);
  return (
    <StyledHeader data-slot="tray-header" {...props}>
      <div>
        <Heading id={headingId}>{title}</Heading>
        {status && <HeaderStatus>{status}</HeaderStatus>}
      </div>
      {count != null && <Badge>{count}</Badge>}
    </StyledHeader>
  );
}

export const TrayCells = styled.ul.attrs({ "data-slot": "tray-cells" })`
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const cellPaddingBlock = "var(--tray-cell-padding-block, var(--juno-space-4))";

const Cell = styled.li`
  position: relative;
`;

// Centres the 1px line under the step's circle
const StepLine = styled.span`
  position: absolute;
  left: calc(var(--juno-space-7) / 2 - 0.5px);
  width: 1px;
  background-color: var(--foreground);
`;

const LineIn = styled(StepLine)`
  top: 0;
  height: ${cellPaddingBlock};

  ${Cell}:first-child & {
    display: none;
  }
`;

// Starts below the step's circle
const LineOut = styled(StepLine)`
  top: calc(${cellPaddingBlock} + var(--juno-space-7));
  bottom: 0;

  ${Cell}:last-child & {
    display: none;
  }
`;

const cellElement = css`
  display: flex;
  width: 100%;
  align-items: stretch;
  gap: var(--juno-space-4);
  padding: 0;
  border: 0;
  border-radius: var(--radius-lg);
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  text-decoration: none;
  cursor: pointer;

  &:focus-visible {
    outline: none;
    /* Mark's ring-[1.5px] */
    box-shadow: 0 0 0 1.5px var(--ring);
  }
`;

const chevronOnHover = css`
  &:hover:not(:disabled) [data-slot="tray-cell-chevron"] {
    color: var(--tray-cell-chevron-hover-color, currentColor);
  }
`;

const CellLink = styled(Link)`
  ${cellElement}
  ${chevronOnHover}

  /* && outranks GlobalStyle's a:link, a:visited, a:hover and a:active */
  &&:link,
  &&:visited,
  &&:hover,
  &&:active {
    color: inherit;
    border-bottom: 0;
  }
`;

const CellButton = styled.button`
  ${cellElement}
  ${chevronOnHover}

  &:disabled {
    color: var(--muted-foreground);
    cursor: default;
  }
`;

const CellPlain = styled.div`
  ${cellElement}
  cursor: default;
`;

const Step = styled.span`
  display: grid;
  width: var(--juno-space-7);
  height: var(--juno-space-7);
  flex-shrink: 0;
  place-items: center;
  margin-top: ${cellPaddingBlock};
  border-radius: 50%;
  background-color: var(--primary);
  color: var(--primary-foreground);
  font-size: var(--text-sm);
  line-height: var(--text-sm--line-height);

  svg {
    width: var(--juno-space-4);
    height: var(--juno-space-4);
  }
`;

const CellBody = styled.span`
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--juno-space-4);
  padding: ${cellPaddingBlock} 0;
  border-bottom: 1px solid var(--border);

  ${Cell}:last-child & {
    border-bottom: 0;
  }

  /* Centred on the title's first line: Mark's 2px, from a 20px icon on a 24px line */
  > svg {
    width: var(--tray-cell-icon-size, var(--juno-space-5));
    height: var(--tray-cell-icon-size, var(--juno-space-5));
    flex-shrink: 0;
    margin-top: calc(
      (var(--tray-cell-title-line-height, var(--text-lg--line-height)) - var(--tray-cell-icon-size, var(--juno-space-5))) /
        2
    );
  }
`;

const CellText = styled.span`
  display: flex;
  min-width: 0;
  flex-direction: column;
`;

const CellTitle = styled.span`
  font-size: var(--tray-cell-title-font-size, var(--text-lg));
  line-height: var(--tray-cell-title-line-height, var(--text-lg--line-height));

  ${CellLink}:hover &,
  ${CellButton}:hover:not(:disabled) & {
    text-decoration: underline;
    text-underline-offset: 0.2em;
  }
`;

const CellStatus = styled.span`
  display: flex;
  align-items: center;
  gap: var(--juno-space-2);
  font-size: var(--text-sm);
  line-height: var(--text-sm--line-height);

  svg {
    width: var(--juno-space-4);
    height: var(--juno-space-4);
    flex-shrink: 0;
  }
`;

const CellDetails = styled.span`
  color: var(--muted-foreground);
`;

export function TrayCell({ step, icon, title, status, details, complete = false, href, disabled = false, ...props }) {
  const numbered = step != null;
  const opens = Boolean(href || props.onClick) && !disabled;
  const content = (
    <>
      {(numbered || icon) && <Step>{icon ?? step}</Step>}
      <CellBody>
        <CellText>
          <CellTitle>{title}</CellTitle>
          {(status || details) && (
            <CellStatus>
              {complete && <CheckIcon aria-hidden />}
              {status && <span>{status}</span>}
              {details && <CellDetails>{details}</CellDetails>}
            </CellStatus>
          )}
        </CellText>
        {opens && <ChevronRightIcon aria-hidden data-slot="tray-cell-chevron" />}
      </CellBody>
    </>
  );

  return (
    <Cell data-slot="tray-cell">
      {numbered && (
        <>
          <LineIn aria-hidden />
          <LineOut aria-hidden />
        </>
      )}
      {href ? (
        <CellLink to={href} {...props}>
          {content}
        </CellLink>
      ) : opens || disabled ? (
        <CellButton type="button" disabled={disabled} {...props}>
          {content}
        </CellButton>
      ) : (
        <CellPlain {...props}>{content}</CellPlain>
      )}
    </Cell>
  );
}

export const TrayActions = styled.div.attrs({ "data-slot": "tray-actions" })`
  display: flex;
  gap: var(--juno-space-2);
  padding-top: var(--tray-actions-padding-top, 0);
  padding-bottom: var(--tray-actions-padding-bottom, 0);

  > * {
    flex: 1;
  }
`;
